import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAuditLog, getAuditSummary } from '../api';
import LoadingState from '../components/LoadingState';
import StateScreen from '../components/ui/StateScreen';
import SegmentedControl from '../components/ui/SegmentedControl';
import { usePageSearchState } from '../lib/pageSearch';

/* ═══════════════════════════════════════════════════════════════════════════
   Audit Log — warm-cream + sage token redesign (single-file page)
   System-wide audit trail: every login / view / create / update / delete /
   status change / export, with who · what · when. Two views (Timeline feed ·
   Dashboard rollups), action-filter tiles, entity filter, search.
   Visual reskin only — data model + API + role gating unchanged.
   Full-width: .app-main supplies the 24px gutter; AppHeader supplies the title.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ─────────────── icons (inline SVG, no glyphs) ─────────────── */
const AlIcon = ({ name, size = 16, sw = 1.6 }) => {
  const p = (d) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>{d}</svg>
  );
  switch (name) {
    case 'key':       return p(<><circle cx="8" cy="15" r="4.5" /><path d="m11 12 8.5-8.5M16 6l2.5 2.5M14 8l2 2" /></>);
    case 'eye':       return p(<><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="3" /></>);
    case 'plus':      return p(<path d="M12 5v14M5 12h14" />);
    case 'edit':      return p(<><path d="M4 20h4L19 9l-4-4L4 16Z" /><path d="m13.5 6.5 4 4" /></>);
    case 'trash':     return p(<><path d="M4 7h16M9 7V5a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 5v2M6 7l1 13a1.5 1.5 0 0 0 1.5 1.4h7A1.5 1.5 0 0 0 17 20L18 7" /><path d="M10 11v6M14 11v6" /></>);
    case 'refresh':   return p(<><path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" /><path d="M3 21v-5h5" /></>);
    case 'upload':    return p(<><path d="M12 16V4" /><path d="m7 9 5-5 5 5" /><path d="M5 16v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2" /></>);
    case 'search':    return p(<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>);
    case 'chevron-d': return p(<path d="m6 9 6 6 6-6" />);
    case 'x':         return p(<path d="M6 6l12 12M6 18 18 6" />);
    case 'arrow-up-r':return p(<><path d="M7 17 17 7" /><path d="M8 7h9v9" /></>);
    default: return null;
  }
};

/* ─────────────── action-tone map (the categorical core) ─────────────── */
const ACTIONS = {
  login:         { label: 'Login',         tone: 'var(--success)',         text: 'var(--success)',         icon: 'key' },
  view:          { label: 'View',          tone: 'var(--text-3)',          text: 'var(--text-2)',          icon: 'eye',     neutral: true },
  create:        { label: 'Create',        tone: 'var(--accent)',          text: 'var(--accent-text)',     icon: 'plus' },
  update:        { label: 'Update',        tone: 'var(--warning)',         text: 'var(--warning)',         icon: 'edit' },
  delete:        { label: 'Delete',        tone: 'var(--danger)',          text: 'var(--danger)',          icon: 'trash' },
  status_change: { label: 'Status Change', tone: 'var(--type-inspection)', text: 'var(--type-inspection)', icon: 'refresh' },
  export:        { label: 'Export',        tone: 'var(--success)',         text: 'var(--success)',         icon: 'upload' },
  _default:      { label: 'Change',        tone: 'var(--text-3)',          text: 'var(--text-2)',          icon: 'edit',    neutral: true },
};
const actionMeta = (k) => ACTIONS[k] || ACTIONS._default;
const actionSoft = (a) => a.neutral ? 'var(--bg-surface-2)' : `color-mix(in oklch, ${a.tone} 14%, var(--bg-surface))`;
const actionRing = (a) => a.neutral ? 'var(--border-subtle)' : `color-mix(in oklch, ${a.tone} 22%, transparent)`;

/* ─────────────── entity labels ─────────────── */
const ENTITY_LABELS = {
  project: 'Project', task: 'Task', billing_period: 'Billing Period',
  user: 'User', sov_line: 'SOV Line', change_order: 'Change Order', forecast: 'Forecast',
};
const entityLabel = (k) => ENTITY_LABELS[k] || k;
const ENTITY_OPTIONS = Object.keys(ENTITY_LABELS).map((k) => ({ value: k, label: ENTITY_LABELS[k] }));

/* ─────────────── formatters ─────────────── */
// created_at is stored UTC; append 'Z' (unless already zoned) so the instant
// is read as UTC and rendered in the viewer's locale.
const fmtTime = (v) => {
  if (v == null) return '—';
  const s = typeof v === 'string' ? (/(Z|[+-]\d\d:?\d\d)$/.test(v) ? v : v + 'Z') : v;
  const x = s instanceof Date ? s : new Date(s);
  if (isNaN(x)) return '—';
  return x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' + x.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};
const dayLabel = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('en-US', { weekday: 'short' }) + ' · ' +
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const initials = (name) => {
  if (!name || name === '—') return '—';
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};
const humanizeKey = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const parseDetails = (details) => {
  if (details == null || details === '') return null;
  try {
    const obj = JSON.parse(details);
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      const pairs = Object.entries(obj);
      if (pairs.length) return { kind: 'kv', pairs };
    }
  } catch (e) { /* not JSON */ }
  return { kind: 'raw', text: String(details) };
};

/* ─────────────── small primitives ─────────────── */
const Avatar = ({ ini, size = 30, accent = false }) => (
  <div style={{
    width: size, height: size, borderRadius: size * 0.28, flex: '0 0 auto',
    background: accent ? 'var(--accent-soft)' : 'var(--bg-surface-2)',
    color: accent ? 'var(--accent-text)' : 'var(--text-2)',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.36, fontWeight: 600, fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em',
    border: '1px solid ' + (accent ? 'color-mix(in oklch, var(--accent) 22%, transparent)' : 'var(--border-subtle)'),
  }}>{ini}</div>
);

const CountChip = ({ children }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 7, height: 28, padding: '0 12px', borderRadius: 999,
    background: 'var(--bg-surface-2)', border: '1px solid var(--border-default)',
    fontSize: 14, fontWeight: 500, color: 'var(--text-2)', whiteSpace: 'nowrap',
  }}>{children}</span>
);

const ActionBadge = ({ action }) => {
  const a = actionMeta(action);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, height: 22, padding: '0 9px 0 7px', borderRadius: 6,
      background: actionSoft(a), border: '1px solid ' + actionRing(a),
      fontSize: 13, fontWeight: 600, color: a.text, whiteSpace: 'nowrap',
    }}>
      <span style={{ display: 'inline-flex', color: a.text }}><AlIcon name={a.icon} size={12} sw={1.8} /></span>
      {a.label}
    </span>
  );
};

const ActionTile = ({ action, count, selected, onClick }) => {
  const a = actionMeta(action);
  const muted = count === 0;
  return (
    <button type="button" onClick={onClick} className="al-tile" style={{
      display: 'flex', flexDirection: 'column', gap: 11, padding: '13px 15px', borderRadius: 10, textAlign: 'left',
      background: selected ? actionSoft(a) : 'var(--bg-surface)',
      border: '1px solid ' + (selected ? actionRing(a) : 'var(--border-default)'),
      boxShadow: selected ? 'none' : 'var(--shadow-xs)', cursor: 'pointer', transition: 'background 120ms, border-color 120ms',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{
          width: 28, height: 28, borderRadius: 8, flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          color: muted ? 'var(--text-4)' : a.text,
          background: muted ? 'var(--bg-surface-2)' : actionSoft(a),
          border: '1px solid ' + (muted ? 'var(--border-subtle)' : actionRing(a)),
        }}>
          <AlIcon name={a.icon} size={15} sw={1.7} />
        </span>
        <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{a.label}</span>
      </div>
      <span className="mono" style={{ fontSize: 27.5, fontWeight: 800, lineHeight: 1, color: muted ? 'var(--text-4)' : a.text, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>{count}</span>
    </button>
  );
};

/* ─────────────── tokenized entity select ─────────────── */
const EntitySelect = ({ value, onChange }) => (
  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="al-select" style={{
      appearance: 'none', WebkitAppearance: 'none', height: 34, padding: '0 34px 0 13px', borderRadius: 8, minWidth: 170,
      fontSize: 14.5, fontWeight: 500, color: value ? 'var(--text-1)' : 'var(--text-2)', fontFamily: 'var(--font-sans)',
      background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer', letterSpacing: '-0.005em',
    }}>
      <option value="">All Types</option>
      {ENTITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    <span style={{ position: 'absolute', right: 11, color: 'var(--text-3)', pointerEvents: 'none', display: 'inline-flex' }}>
      <AlIcon name="chevron-d" size={14} />
    </span>
  </div>
);

/* ─────────────── tokenized search input ─────────────── */
const SearchInput = ({ value, onChange, onSubmit }) => (
  <div className="al-search" style={{
    display: 'flex', alignItems: 'center', gap: 8, height: 34, padding: '0 11px', borderRadius: 8, minWidth: 260,
    background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)',
  }}>
    <span style={{ color: 'var(--text-3)', flex: '0 0 auto', display: 'inline-flex' }}><AlIcon name="search" size={15} /></span>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') onSubmit(); }}
      placeholder="Search names, users, details…"
      style={{ flex: 1, minWidth: 0, height: '100%', border: 0, outline: 'none', background: 'transparent', fontSize: 14.5, fontFamily: 'var(--font-sans)', color: 'var(--text-1)', letterSpacing: '-0.005em' }}
    />
    {value && (
      <button type="button" className="al-searchclear" onClick={() => { onChange(''); onSubmit(''); }} aria-label="Clear search" style={{
        flex: '0 0 auto', width: 20, height: 20, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--text-3)', background: 'var(--bg-surface-2)', border: 'none', cursor: 'pointer',
      }}>
        <AlIcon name="x" size={11} sw={2} />
      </button>
    )}
  </div>
);

/* ─────────────── details line ─────────────── */
const DetailsLine = ({ details }) => {
  const parsed = parseDetails(details);
  if (!parsed) return null;
  if (parsed.kind === 'raw') return <span style={{ fontSize: 13.5, color: 'var(--text-3)' }}>{parsed.text}</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      {parsed.pairs.map(([k, v]) => (
        <span key={k} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5 }}>
          <span style={{ fontSize: 13, color: 'var(--text-3)' }}>{humanizeKey(k)}:</span>
          <span className="mono" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>{String(v)}</span>
        </span>
      ))}
    </span>
  );
};

/* ─────────────── timeline event row ─────────────── */
const EventRow = ({ ev, last, onOpenProject }) => {
  const a = actionMeta(ev.action);
  const showProjectLink = ev.entity_type !== 'project' && ev.project_id != null;
  return (
    <div style={{ display: 'flex', gap: 14, position: 'relative' }}>
      <div style={{ flex: '0 0 auto', width: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4 }}>
        <span style={{ width: 11, height: 11, borderRadius: 999, flex: '0 0 auto', background: actionSoft(a), border: '2px solid ' + a.tone, zIndex: 1 }} />
        {!last && <span style={{ flex: 1, width: 2, background: 'var(--border-subtle)', marginTop: 3 }} />}
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingBottom: last ? 0 : 18 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
              <ActionBadge action={ev.action} />
              {ev.entity_type && <span style={{ fontSize: 13.5, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{entityLabel(ev.entity_type)}</span>}
              {ev.entity_name && <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.entity_name}</span>}
              {showProjectLink && (
                <button type="button" className="al-projlink" onClick={() => onOpenProject && onOpenProject(ev.project_id)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600,
                  color: 'var(--accent-text)', whiteSpace: 'nowrap', flex: '0 0 auto', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                }}>
                  <span className="mono" style={{ fontVariantNumeric: 'tabular-nums' }}>Project #{ev.project_id}</span>
                  <AlIcon name="arrow-up-r" size={12} sw={1.9} />
                </button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {ev.user_name && <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{ev.user_name}</span>}
              {ev.user_name && parseDetails(ev.details) && <span style={{ width: 3, height: 3, borderRadius: 999, background: 'var(--text-4)', flex: '0 0 auto' }} />}
              <DetailsLine details={ev.details} />
            </div>
          </div>
          <span className="mono" style={{ flex: '0 0 auto', fontSize: 12.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', paddingTop: 2 }}>{fmtTime(ev.created_at)}</span>
        </div>
      </div>
    </div>
  );
};

/* ─────────────── cards (timeline + dashboard) ─────────────── */
const Card = ({ header, children, pad = true }) => (
  <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
    {header && <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', flexWrap: 'wrap' }}>{header}</div>}
    <div style={{ padding: pad ? 16 : 0, flex: 1 }}>{children}</div>
  </div>
);
const CardTitle = ({ children }) => <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-2)' }}>{children}</span>;
const EmptyInline = ({ children }) => <div style={{ padding: '28px 4px', textAlign: 'center', fontSize: 14, color: 'var(--text-3)' }}>{children}</div>;

/* ── dashboard cards ── */
const DailyActivity = ({ byDay }) => {
  if (!byDay || !byDay.length) return <EmptyInline>No activity yet.</EmptyInline>;
  const max = Math.max(...byDay.map((d) => d.count), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      {byDay.map((d) => {
        const muted = d.count === 0;
        return (
          <div key={d.day} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="mono" style={{ flex: '0 0 auto', width: 112, fontSize: 12.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{dayLabel(d.day)}</span>
            <div style={{ flex: 1, minWidth: 0, height: 18, borderRadius: 5, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
              <div style={{ width: Math.max((d.count / max) * 100, muted ? 0 : 3) + '%', height: '100%', borderRadius: 4, background: muted ? 'transparent' : 'color-mix(in oklch, var(--accent) 80%, var(--bg-surface))' }} />
            </div>
            <span className="mono" style={{ flex: '0 0 auto', width: 34, textAlign: 'right', fontSize: 13.5, fontWeight: 600, color: muted ? 'var(--text-4)' : 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>{d.count}</span>
          </div>
        );
      })}
    </div>
  );
};

const MostActiveUsers = ({ byUser }) => {
  if (!byUser || !byUser.length) return <EmptyInline>No user activity yet.</EmptyInline>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {byUser.map((u, i) => (
        <div key={u.user_id || i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
          <Avatar ini={initials(u.user_name)} size={30} accent />
          <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.user_name || 'Unknown'}</span>
          <span style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'baseline', gap: 5 }}>
            <span className="mono" style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--accent-text)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>{u.count}</span>
            <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>events</span>
          </span>
        </div>
      ))}
    </div>
  );
};

const ActivityByType = ({ byEntityType }) => {
  if (!byEntityType || !byEntityType.length) return <EmptyInline>No activity yet.</EmptyInline>;
  const max = Math.max(...byEntityType.map((t) => t.count), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {byEntityType.map((t, i) => (
        <div key={t.entity_type} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
          <span style={{ flex: '0 0 auto', width: 116, fontSize: 14, fontWeight: 500, color: 'var(--text-1)', whiteSpace: 'nowrap' }}>{entityLabel(t.entity_type)}</span>
          <div style={{ flex: 1, minWidth: 0, height: 6, borderRadius: 999, background: 'var(--bg-surface-2)', overflow: 'hidden' }}>
            <div style={{ width: Math.max((t.count / max) * 100, 3) + '%', height: '100%', borderRadius: 999, background: 'color-mix(in oklch, var(--text-3) 55%, var(--bg-surface))' }} />
          </div>
          <span className="mono" style={{ flex: '0 0 auto', width: 40, textAlign: 'right', fontSize: 14, fontWeight: 600, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>{t.count}</span>
        </div>
      ))}
    </div>
  );
};

const RecentLogins = ({ recentLogins }) => {
  if (!recentLogins || !recentLogins.length) return <EmptyInline>No recent logins.</EmptyInline>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {recentLogins.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 0', borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
          <span style={{
            width: 26, height: 26, borderRadius: 7, flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--success)', background: 'color-mix(in oklch, var(--success) 14%, var(--bg-surface))', border: '1px solid color-mix(in oklch, var(--success) 22%, transparent)',
          }}>
            <AlIcon name="key" size={13} sw={1.8} />
          </span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.user_name}</span>
          <span className="mono" style={{ flex: '0 0 auto', fontSize: 12.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtTime(r.created_at)}</span>
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Page
   ═══════════════════════════════════════════════════════════════════════════ */
const AuditLogPage = ({ onOpenProject }) => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('timeline'); // timeline | dashboard

  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  // One person's day, as Team Activity opens it (?user_id=&day=, a Central day).
  const [params, setParams] = useSearchParams();
  const personId = params.get('user_id') || '';
  const personDay = /^\d{4}-\d{2}-\d{2}$/.test(params.get('day') || '') ? params.get('day') : '';
  // The box below and the top bar's search (lib/pageSearch) are one text.
  const [searchInput, setSearchInput] = usePageSearchState('Search names, users, details…');

  const fetchSeq = useRef(0); // only the newest request's answer is shown
  const fetchData = useCallback(async () => {
    const seq = ++fetchSeq.current;
    try {
      setLoading(true);
      const query = { limit: 200 };
      if (filterAction) query.action = filterAction;
      if (filterEntity) query.entity_type = filterEntity;
      if (filterSearch) query.search = filterSearch;
      if (personId) query.user_id = personId;
      if (personDay) query.day = personDay;
      const [logData, summaryData] = await Promise.all([getAuditLog(query), getAuditSummary()]);
      if (seq !== fetchSeq.current) return;
      setLogs(Array.isArray(logData) ? logData : []);
      setSummary(summaryData);
    } catch (e) { console.error(e); }
    finally { if (seq === fetchSeq.current) setLoading(false); }
  }, [filterAction, filterEntity, filterSearch, personId, personDay]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // The top bar has no Search button (it filters as you type), so a pause in typing runs the
  // same server search as Enter / Search.
  useEffect(() => {
    const t = setTimeout(() => setFilterSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const commitSearch = (v) => setFilterSearch(v !== undefined ? v : searchInput);
  const clearPerson = () => setParams({}, { replace: true });
  const clearAll = () => { setFilterAction(''); setFilterEntity(''); setFilterSearch(''); setSearchInput(''); clearPerson(); };
  const anyFilter = !!(filterAction || filterEntity || filterSearch || personId || personDay);

  if (loading && !summary) {
    return <LoadingState message="Loading audit log…" padding="80px 20px" />;
  }

  const totalEvents = summary?.totalEvents || 0;
  const byAction = summary?.byAction || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        .al-tile:hover { border-color: var(--border-strong) !important; background: var(--bg-hover) !important; }
        .al-projlink:hover { text-decoration: underline; }
        .al-ghost:hover { border-color: color-mix(in oklch, var(--accent) 35%, var(--border-default)); color: var(--accent-text); background: var(--accent-soft); }
        .al-primary:hover { background: var(--accent-hover); }
        .al-searchclear:hover { color: var(--text-1); background: var(--bg-hover); }
        .al-search:focus-within { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
        .al-select:focus-visible { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
      `}</style>

      {/* Region 1 — subtitle + total chip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <p style={{ margin: 0, fontSize: 15, color: 'var(--text-3)', flex: 1, minWidth: 220 }}>
          Every login, view, and change across the system — fully auditable.
        </p>
        <CountChip>
          <span className="mono" style={{ fontWeight: 700, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>{totalEvents.toLocaleString()}</span>
          total events
        </CountChip>
      </div>

      {(personId || personDay) && (
        <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '9px 12px', borderRadius: 9, fontSize: 13.5, color: 'var(--text-2)', background: 'var(--accent-soft)', border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)' }}>
          <span style={{ flex: 1, minWidth: 200 }}>
            Showing {logs[0] && logs[0].user_name ? <strong style={{ fontWeight: 600 }}>{logs[0].user_name}</strong> : 'one person'}
            {personDay ? ` on ${new Date(`${personDay}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}` : ''}, from Team Activity.
          </span>
          <button type="button" className="al-ghost" onClick={clearPerson} style={{ height: 30, padding: '0 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, color: 'var(--text-2)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', cursor: 'pointer' }}>Show everyone</button>
        </div>
      )}

      {/* Region 2 — action filter tiles */}
      {byAction.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
          {byAction.map((b) => (
            <ActionTile key={b.action} action={b.action} count={b.count}
              selected={filterAction === b.action}
              onClick={() => setFilterAction(filterAction === b.action ? '' : b.action)} />
          ))}
        </div>
      )}

      {/* Region 3 — filter bar */}
      <section style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '10px 14px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)',
      }}>
        <SegmentedControl value={view} onChange={setView} options={[{ value: 'timeline', label: 'Timeline' }, { value: 'dashboard', label: 'Dashboard' }]} />
        <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--border-subtle)', margin: '2px 2px', flex: '0 0 auto' }} />
        <EntitySelect value={filterEntity} onChange={setFilterEntity} />
        <div style={{ flex: 1, minWidth: 8 }} />
        <SearchInput value={searchInput} onChange={setSearchInput} onSubmit={commitSearch} />
        <button type="button" className="al-primary" onClick={() => commitSearch()} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 15px', borderRadius: 8,
          fontSize: 14, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', background: 'var(--accent)',
          border: '1px solid color-mix(in oklch, var(--accent) 50%, oklch(0.3 0.05 135))',
          boxShadow: '0 1px 0 oklch(0.42 0.06 135), 0 1px 2px oklch(0.4 0.05 135 / 0.25)', cursor: 'pointer',
        }}>Search</button>
        {anyFilter && (
          <button type="button" className="al-ghost" onClick={clearAll} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 13px', borderRadius: 8,
            fontSize: 14, fontWeight: 500, color: 'var(--text-2)', whiteSpace: 'nowrap',
            background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer',
          }}>Clear</button>
        )}
      </section>

      {/* Region 4 — view body */}
      {view === 'timeline' ? (
        logs.length === 0 ? (
          <Card>
            <StateScreen
              kind="empty"
              title="No events found"
              body={anyFilter
                ? 'Try adjusting your filters — clear the action, entity, or search to see the full trail.'
                : 'Events will appear here as people log in, view, and change records across the system.'}
              primary={anyFilter ? { label: 'Clear filters', onClick: clearAll } : null}
            />
          </Card>
        ) : (
          <Card header={<><CardTitle>Event feed</CardTitle><div style={{ flex: 1 }} /><CountChip>{logs.length} {logs.length === 1 ? 'event' : 'events'}</CountChip></>}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {logs.map((ev, i) => (
                <EventRow key={ev.id} ev={ev} last={i === logs.length - 1} onOpenProject={onOpenProject} />
              ))}
            </div>
          </Card>
        )
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
          <Card header={<CardTitle>Daily Activity — last 30 days</CardTitle>}><DailyActivity byDay={summary?.byDay} /></Card>
          <Card header={<CardTitle>Most Active Users</CardTitle>}><MostActiveUsers byUser={summary?.byUser} /></Card>
          <Card header={<CardTitle>Activity by Type</CardTitle>}><ActivityByType byEntityType={summary?.byEntityType} /></Card>
          <Card header={<CardTitle>Recent Logins</CardTitle>}><RecentLogins recentLogins={summary?.recentLogins} /></Card>
        </div>
      )}
    </div>
  );
};

export default AuditLogPage;
